import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { loanService } from '../../services/loanService'

function LoanDetail() {
  const { noPinjaman } = useParams()
  const navigate = useNavigate()
  const [loan, setLoan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const [selectedAngsuran, setSelectedAngsuran] = useState(null)
  const [jumlahBayar, setJumlahBayar] = useState(0)
  const [denda, setDenda] = useState(0)

  useEffect(() => {
    fetchLoan()
  }, [noPinjaman])

  const fetchLoan = async () => {
    try {
      setLoading(true)
      const response = await loanService.getById(noPinjaman)
      setLoan(response.data.data)
    } catch (error) {
      console.error('Error fetching loan:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (disetujui) => {
    if (!confirm(`Yakin ingin ${disetujui ? 'menyetujui' : 'menolak'} pinjaman ini?`)) return

    setProcessing(true)
    try {
      await loanService.approve(noPinjaman, disetujui)
      alert(`Pinjaman berhasil ${disetujui ? 'disetujui' : 'ditolak'}`)
      fetchLoan()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal memproses')
    } finally {
      setProcessing(false)
    }
  }

  const handlePay = async () => {
    setProcessing(true)
    try {
      await loanService.payInstallment(noPinjaman, selectedAngsuran.AngsuranKe, jumlahBayar, denda)
      alert('Pembayaran berhasil')
      setShowPayModal(false)
      setSelectedAngsuran(null)
      setJumlahBayar(0)
      setDenda(0)
      fetchLoan()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal memproses pembayaran')
    } finally {
      setProcessing(false)
    }
  }

  const openPayModal = (angsuran) => {
    setSelectedAngsuran(angsuran)
    setJumlahBayar(angsuran.TotalBayar)
    setDenda(0)
    setShowPayModal(true)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID')
  }

  const getStatusBadge = (status) => {
    const badges = {
      'Pengajuan': 'badge-warning',
      'Disetujui': 'badge-info',
      'Ditolak': 'badge-danger',
      'Aktif': 'badge-primary',
      'Lunas': 'badge-success',
      'Macet': 'badge-dark'
    }
    return badges[status] || 'badge-secondary'
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x"></i>
        <p className="mt-2">Memuat data...</p>
      </div>
    )
  }

  if (!loan) {
    return (
      <div className="alert alert-warning">
        Pinjaman tidak ditemukan
      </div>
    )
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Detail Pinjaman</h3>
          <div className="card-tools">
            {loan.Status === 'Pengajuan' && (
              <>
                <button
                  className="btn btn-success btn-sm mr-2"
                  onClick={() => handleApprove(true)}
                  disabled={processing}
                >
                  <i className="fas fa-check mr-1"></i> Setujui
                </button>
                <button
                  className="btn btn-danger btn-sm mr-2"
                  onClick={() => handleApprove(false)}
                  disabled={processing}
                >
                  <i className="fas fa-times mr-1"></i> Tolak
                </button>
              </>
            )}
            <button
              className="btn btn-default btn-sm"
              onClick={() => navigate('/loans/reguler')}
            >
              <i className="fas fa-arrow-left mr-1"></i> Kembali
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td width="150"><strong>No. Pinjaman</strong></td>
                    <td>: {loan.NoPinjaman}</td>
                  </tr>
                  <tr>
                    <td><strong>NBA</strong></td>
                    <td>: {loan.NBA}</td>
                  </tr>
                  <tr>
                    <td><strong>Nama Anggota</strong></td>
                    <td>: {loan.NamaAnggota}</td>
                  </tr>
                  <tr>
                    <td><strong>Jenis Pinjaman</strong></td>
                    <td>: {loan.NamaJenis}</td>
                  </tr>
                  <tr>
                    <td><strong>Status</strong></td>
                    <td>:
                      <span className={`badge ml-1 ${getStatusBadge(loan.Status)}`}>
                        {loan.Status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td width="150"><strong>Plafond</strong></td>
                    <td>: {formatCurrency(loan.Plafond)}</td>
                  </tr>
                  <tr>
                    <td><strong>Bunga</strong></td>
                    <td>: {loan.Bunga}% / tahun ({loan.TipeBunga})</td>
                  </tr>
                  <tr>
                    <td><strong>Tenor</strong></td>
                    <td>: {loan.Tenor} bulan</td>
                  </tr>
                  <tr>
                    <td><strong>Angsuran/bulan</strong></td>
                    <td>: {formatCurrency(loan.TotalAngsuran)}</td>
                  </tr>
                  <tr>
                    <td><strong>Tgl Pencairan</strong></td>
                    <td>: {formatDate(loan.TglPencairan)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-4">
              <div className="info-box bg-info">
                <span className="info-box-icon"><i className="fas fa-money-bill"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Sisa Pokok</span>
                  <span className="info-box-number">{formatCurrency(loan.SisaPokok)}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-box bg-warning">
                <span className="info-box-icon"><i className="fas fa-calendar"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Sisa Tenor</span>
                  <span className="info-box-number">{loan.SisaTenor} bulan</span>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="info-box bg-success">
                <span className="info-box-icon"><i className="fas fa-percentage"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Terbayar</span>
                  <span className="info-box-number">
                    {Math.round(((loan.Tenor - loan.SisaTenor) / loan.Tenor) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {loan.angsuran && loan.angsuran.length > 0 && (
            <div className="mt-4">
              <h5>Jadwal Angsuran</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="thead-light">
                    <tr>
                      <th>Ke</th>
                      <th>Jatuh Tempo</th>
                      <th>Pokok</th>
                      <th>Bunga</th>
                      <th>Total</th>
                      <th>Tgl Bayar</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(loan.angsuran) && loan.angsuran.map((a) => (
                      <tr key={a.AngsuranKe}>
                        <td>{a.AngsuranKe}</td>
                        <td>{formatDate(a.TglJatuhTempo)}</td>
                        <td className="text-right">{formatCurrency(a.Pokok)}</td>
                        <td className="text-right">{formatCurrency(a.Bunga)}</td>
                        <td className="text-right">{formatCurrency(a.TotalBayar)}</td>
                        <td>{formatDate(a.TglBayar)}</td>
                        <td>
                          <span className={`badge ${
                            a.Status === 'Terbayar' ? 'badge-success' :
                            a.Status === 'Telat' ? 'badge-danger' :
                            a.Status === 'JatuhTempo' ? 'badge-warning' :
                            'badge-secondary'
                          }`}>
                            {a.Status}
                          </span>
                        </td>
                        <td>
                          {loan.Status === 'Aktif' && a.Status !== 'Terbayar' && (
                            <button
                              className="btn btn-success btn-xs"
                              onClick={() => openPayModal(a)}
                            >
                              <i className="fas fa-money-bill-wave"></i> Bayar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {showPayModal && selectedAngsuran && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Bayar Angsuran Ke-{selectedAngsuran.AngsuranKe}</h5>
                <button type="button" className="close text-white" onClick={() => setShowPayModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Tagihan Pokok</label>
                  <input type="text" className="form-control" value={formatCurrency(selectedAngsuran.Pokok)} readOnly />
                </div>
                <div className="form-group">
                  <label>Tagihan Bunga</label>
                  <input type="text" className="form-control" value={formatCurrency(selectedAngsuran.Bunga)} readOnly />
                </div>
                <div className="form-group">
                  <label>Denda</label>
                  <input
                    type="number"
                    className="form-control"
                    value={denda}
                    onChange={(e) => setDenda(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="form-group">
                  <label>Total Bayar *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={jumlahBayar + denda}
                    onChange={(e) => setJumlahBayar(parseInt(e.target.value) - denda || 0)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPayModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handlePay}
                  disabled={processing}
                >
                  {processing ? 'Memproses...' : 'Bayar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoanDetail
